import { Box, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getMissingApprovals, submitApproval } from 'lib/api/approvals';
import { APPROVAL_TYPES, COMITEE_TYPES } from 'lib/api/approvals/types';
import { getRecieverFactoringRequestOverview } from 'lib/api/factoring';
import {
  BackBubbleButton,
  CustomButton,
  ErrorState,
  getStatusChipColor,
  HeroHeader,
  MobileSelect,
  MobileTextArea,
} from 'lib/mobileDesignSystem';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import type { ApprovalFormState, ApprovalLocationState } from './types';
import { reasonRules, roleSelectRules } from './validation';
import { isApprovalFormValid } from './validation';

const APPROVAL_TYPE_APPROVE: ApprovalLocationState['type'] = 'approve';

const FactoringApproval: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const state = location.state as ApprovalLocationState | undefined;
  const typeFromState = state?.type;
  const typeFromQuery = searchParams.get('type') as
    | ApprovalLocationState['type']
    | null;
  const type: ApprovalLocationState['type'] =
    typeFromState ??
    (typeFromQuery === 'reject' ? 'reject' : APPROVAL_TYPE_APPROVE);

  const { data: overview } = getRecieverFactoringRequestOverview(dispatch, {
    requestId: id ?? '',
  });
  const quoteIdFromOverview = overview?.quoteId ?? null;
  const quoteId = state?.quoteId ?? quoteIdFromOverview ?? 0;

  const { data: missingApprovals } = getMissingApprovals(
    dispatch,
    APPROVAL_TYPES.FACTORING_QUOTE,
    quoteId,
    COMITEE_TYPES.RISK,
  );

  const userRolesFromState = state?.userRoles;
  const userRolesFromApi = useMemo(() => {
    if (!missingApprovals?.roles) return [];
    return missingApprovals.roles
      .filter((r) => r.userCanApprove)
      .map((r) => ({ id: r.roleId, name: r.roleName }));
  }, [missingApprovals?.roles]);
  const userRoles = userRolesFromState ?? userRolesFromApi;

  const [form, setForm] = useState<ApprovalFormState>({
    roleId: null,
    reason: '',
  });

  useEffect(() => {
    if (userRoles.length > 0 && form.roleId === null) {
      setForm((prev) => ({ ...prev, roleId: userRoles[0].id }));
    }
  }, [userRoles, form.roleId]);

  const { mutate: submitApprovalMutation, isPending: isSubmitting } =
    submitApproval(
      dispatch,
      APPROVAL_TYPES.FACTORING_QUOTE,
      quoteId,
      COMITEE_TYPES.RISK,
    );

  const fadeColor =
    type === APPROVAL_TYPE_APPROVE
      ? getStatusChipColor('success')
      : getStatusChipColor('error');
  const title = `Aprobación #${id ?? ''}`;

  const isFormValid = isApprovalFormValid(form);
  const canSubmit =
    isFormValid && !isSubmitting && quoteId > 0 && userRoles.length > 0;

  const handleConfirm = () => {
    if (!canSubmit || form.roleId == null) return;
    submitApprovalMutation(
      {
        decision: type === APPROVAL_TYPE_APPROVE ? 'APPROVED' : 'REJECTED',
        roleId: form.roleId,
        message: form.reason.trim(),
      },
      {
        onSuccess: () => {
          if (id) {
            void queryClient.invalidateQueries({
              queryKey: ['factoring', 'request', id, 'overview'],
            });
          }
          navigate(-1);
        },
      },
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const hasError = !id || (overview != null && quoteId <= 0);
  if (hasError) {
    return <ErrorState message="No se pudo cargar la operación." />;
  }
  if (overview === undefined) {
    return null;
  }

  return (
    <Stack
      sx={{
        minHeight: '100%',
        pb: 12,
        px: 0,
      }}
      spacing={0}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title={title}
          minHeight={60}
          fadeColor={fadeColor}
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={handleBack} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ px: 2, pt: 2, flex: 1 }}>
        {userRoles.length === 0 ? (
          <ErrorState message="No hay roles disponibles para firmar esta aprobación." />
        ) : (
          <>
            <MobileSelect
              label="Firmas como:"
              placeholder="Rol de aprobación"
              options={userRoles}
              value={form.roleId}
              onChange={(roleId) => setForm((prev) => ({ ...prev, roleId }))}
              validationRules={roleSelectRules}
              required
            />
            <MobileTextArea
              label="Razón:"
              placeholder={
                type === APPROVAL_TYPE_APPROVE
                  ? 'Describe por qué apruebas esta solicitud...'
                  : 'Describe por qué rechazas esta solicitud...'
              }
              value={form.reason}
              onChange={(reason) => setForm((prev) => ({ ...prev, reason }))}
              validationRules={reasonRules}
              rows={4}
              required
            />
          </>
        )}
      </Stack>

      {userRoles.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
          }}
        >
          <CustomButton
            variant="primary"
            fullWidth
            onClick={handleConfirm}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Confirmar
          </CustomButton>
        </Box>
      )}
    </Stack>
  );
};

export default FactoringApproval;
